import React, { useState } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Upload, Download, Trash2, FileSpreadsheet, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';
import * as XLSX from 'xlsx';

const BatteryImport = () => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [batteries, setBatteries] = useState([]);
  const [preview, setPreview] = useState([]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setFile(selectedFile);
    parseExcelFile(selectedFile);
  };

  const parseExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          toast.error('Excel file is empty');
          return;
        }

        // Map Excel columns to battery format
        const mappedBatteries = jsonData.map((row, index) => ({
          serial_number: row['Serial Number'] || row['serial_number'] || '',
          battery_id: row['Battery ID'] || row['battery_id'] || `BAT${String(index + 1).padStart(4, '0')}`,
          capacity_ah: parseFloat(row['Capacity (Ah)'] || row['capacity_ah'] || 35),
          voltage: parseFloat(row['Voltage'] || row['voltage'] || 48),
          status: row['Status'] || row['status'] || 'available',
          health_percentage: parseFloat(row['Health %'] || row['health_percentage'] || 100),
          charge_cycles: parseInt(row['Charge Cycles'] || row['charge_cycles'] || 0),
          purchase_date: row['Purchase Date'] || row['purchase_date'] || '2024-01-01'
        }));

        // Validate required fields
        const validBatteries = mappedBatteries.filter(b => b.serial_number);
        
        if (validBatteries.length === 0) {
          toast.error('No valid batteries found. Serial Number column is required.');
          return;
        }

        setBatteries(validBatteries);
        setPreview(validBatteries.slice(0, 10)); // Show first 10 as preview
        toast.success(`✅ ${validBatteries.length} batteries loaded from Excel`);
      } catch (error) {
        toast.error('Failed to parse Excel file');
        console.error(error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (batteries.length === 0) {
      toast.error('No batteries to import');
      return;
    }

    setImporting(true);
    try {
      const response = await axios.post(
        `${API}/batteries/import-excel`,
        { batteries: batteries },
        { headers: getAuthHeader() }
      );

      setImportResult(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`✅ Imported ${response.data.success_count} batteries successfully!`);
      }
      
      if (response.data.failed_count > 0) {
        toast.warning(`⚠️ ${response.data.failed_count} batteries failed to import`);
      }

      // Reset after successful import
      if (response.data.failed_count === 0) {
        setFile(null);
        setBatteries([]);
        setPreview([]);
      }
    } catch (error) {
      toast.error('Failed to import batteries');
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteTestData = async () => {
    if (!window.confirm('Are you sure you want to delete ALL test batteries? This cannot be undone.')) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/batteries/test-data`, { headers: getAuthHeader() });
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to delete test batteries');
    }
  };

  const downloadTemplate = () => {
    const template = [
      {
        'Serial Number': 'EMBY2510001',
        'Battery ID': 'BAT0001',
        'Capacity (Ah)': 35,
        'Voltage': 48,
        'Status': 'available',
        'Health %': 100,
        'Charge Cycles': 0,
        'Purchase Date': '2024-01-01'
      },
      {
        'Serial Number': 'EM24010001',
        'Battery ID': 'BAT0002',
        'Capacity (Ah)': 40,
        'Voltage': 52,
        'Status': 'available',
        'Health %': 95,
        'Charge Cycles': 50,
        'Purchase Date': '2024-01-15'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Batteries');
    XLSX.writeFile(wb, 'battery_import_template.xlsx');
    toast.success('Template downloaded!');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Battery Import</h1>
          <p className="text-slate-600 mt-1">Import batteries from Excel file</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
          <Button variant="destructive" onClick={handleDeleteTestData}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Test Batteries
          </Button>
        </div>
      </div>

      {/* Upload Card */}
      <Card>
        <CardHeader>
          <CardTitle>Step 1: Upload Excel File</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <FileSpreadsheet className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <div><strong>Required Column:</strong> Serial Number</div>
                <div><strong>Optional Columns:</strong> Battery ID, Capacity (Ah), Voltage, Status, Health %, Charge Cycles, Purchase Date</div>
                <div className="text-sm text-slate-600">Download the template above to see the correct format.</div>
              </div>
            </AlertDescription>
          </Alert>

          <div className="flex items-center gap-4">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              id="battery-excel-upload"
            />
            <label
              htmlFor="battery-excel-upload"
              className="flex items-center justify-center px-6 py-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-lime-500 hover:bg-lime-50 transition-colors w-full"
            >
              <div className="text-center">
                <Upload className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                <div className="text-lg font-semibold text-slate-700">
                  {file ? file.name : 'Click to upload Excel file'}
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Supports .xlsx and .xls files
                </div>
              </div>
            </label>
          </div>

          {batteries.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-800 font-semibold">
                <CheckCircle className="w-5 h-5" />
                {batteries.length} batteries ready to import
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Card */}
      {preview.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 2: Preview (First 10 Batteries)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border p-2 text-left">#</th>
                    <th className="border p-2 text-left">Serial Number</th>
                    <th className="border p-2 text-left">Battery ID</th>
                    <th className="border p-2 text-left">Capacity</th>
                    <th className="border p-2 text-left">Voltage</th>
                    <th className="border p-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((battery, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="border p-2">{index + 1}</td>
                      <td className="border p-2 font-mono">{battery.serial_number}</td>
                      <td className="border p-2">{battery.battery_id}</td>
                      <td className="border p-2">{battery.capacity_ah} Ah</td>
                      <td className="border p-2">{battery.voltage} V</td>
                      <td className="border p-2">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                          {battery.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {batteries.length > 10 && (
              <p className="text-sm text-slate-600 mt-2">
                ... and {batteries.length - 10} more batteries
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Button */}
      {batteries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 3: Import</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert className="mb-4 bg-yellow-50 border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                <strong>Important:</strong> This will delete all test batteries (marked with is_test_data flag) 
                and import the batteries from your Excel file.
              </AlertDescription>
            </Alert>
            
            <Button 
              onClick={handleImport} 
              disabled={importing}
              className="bg-gradient-to-r from-lime-600 to-green-600 w-full"
              size="lg"
            >
              {importing ? 'Importing...' : `Import ${batteries.length} Batteries`}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Import Result */}
      {importResult && (
        <Card>
          <CardHeader>
            <CardTitle>Import Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <div className="font-semibold">Success</div>
                    <div className="text-2xl font-bold">{importResult.success_count}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-800">
                  <XCircle className="w-5 h-5" />
                  <div>
                    <div className="font-semibold">Failed</div>
                    <div className="text-2xl font-bold">{importResult.failed_count}</div>
                  </div>
                </div>
              </div>
            </div>

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="font-semibold text-red-800 mb-2">Errors:</div>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                  {importResult.errors.slice(0, 10).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                  {importResult.errors.length > 10 && (
                    <li>... and {importResult.errors.length - 10} more errors</li>
                  )}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-cyan-50">
        <CardHeader>
          <CardTitle>How to Use</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>✓ <strong>Step 1:</strong> Download the template to see the correct Excel format</div>
          <div>✓ <strong>Step 2:</strong> Fill in your battery data (Serial Number is required)</div>
          <div>✓ <strong>Step 3:</strong> Upload the Excel file</div>
          <div>✓ <strong>Step 4:</strong> Preview the data to ensure it looks correct</div>
          <div>✓ <strong>Step 5:</strong> Click "Import" to add batteries to the database</div>
          <div className="mt-3 p-2 bg-yellow-100 rounded">
            ⚠️ <strong>Note:</strong> All test batteries will be deleted before importing your real data.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BatteryImport;
